import { motion } from "framer-motion";
import {
  Brain, Code2, Cloud, Database, FileText, GitBranch, Server,
  Workflow, Sparkles, Zap, Activity,
} from "lucide-react";
import MetricCard from "../components/MetricCard";
import ToolsMarquee from "../components/ToolsMarquee";
import { SERVICES, SERVICE_TOTALS } from "../data/services";
import { REPOS } from "../data/repos";
import { LITELLM_FACTS, AI_ROUTER_AGENTS } from "../data/aiStack";
import { Link } from "react-router-dom";

export default function Overview() {
  const recent = [
    { kind: "deploy", text: "willbracken-admin scaffold created", at: "now" },
    { kind: "ssh",    text: "MacBook → i9 SSH verified (192.168.1.21)", at: "10m ago" },
    { kind: "auth",   text: "Entra IdP added to willbracken.com Access app", at: "1h ago" },
    { kind: "ml",     text: "Fine-tune wb-infra-v2 succeeded (gpt-4o-mini)", at: "today" },
    { kind: "data",   text: "1,330 files batch-embedded on RTX 4080 (90s)", at: "yesterday" },
  ];

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="eyebrow mb-3 flex items-center gap-2">
            <Sparkles size={11} className="text-accent-violet" />
            HUB · v3.0 · MISSION CONTROL
          </div>
          <h1 className="text-4xl lg:text-6xl font-black tracking-tight gradient-text">
            Bracken Platform
          </h1>
          <p className="mt-3 text-slate-400 max-w-2xl text-[15px] leading-relaxed">
            Personal infrastructure overview. Everything deployed, every link verified,
            every metric live. Start with the Stack, follow connections, drill into anything.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {[
              ["Cloudflare", "27 workers"],
              ["Azure", "ACA · AOAI · AI Search"],
              ["Supabase", "35,710 docs"],
              ["Ollama i9", "13 local models"],
              ["ai-router", "43 agents"],
            ].map(([k, v]) => (
              <span key={k} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-xs font-mono">
                <span className="text-slate-400">{k}</span>{" "}
                <span className="text-white">{v}</span>
              </span>
            ))}
          </div>
        </motion.div>

        {/* Decorative orbits */}
        <div className="hidden lg:block absolute -right-24 -top-24 w-96 h-96 rounded-full border border-white/[0.04] animate-spin-slow" />
        <div className="hidden lg:block absolute -right-16 -top-16 w-80 h-80 rounded-full border border-white/[0.03]" />
      </div>

      <ToolsMarquee />

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Services Healthy" value={SERVICE_TOTALS.healthy} unit={`/ ${SERVICE_TOTALS.total}`} icon={Activity} accent="text-accent-emerald" />
        <MetricCard label="Routable Models"   value={LITELLM_FACTS.registeredModels} icon={Brain} accent="text-accent-violet" />
        <MetricCard label="Agents in Router"  value={AI_ROUTER_AGENTS.total} unit="across 7 tiers" icon={Zap} accent="text-accent-cyan" />
        <MetricCard label="Owned Repositories"value={REPOS.length} unit="repos" icon={Code2} accent="text-accent-blue" />
        <MetricCard label="Embedded Documents" value={34977} icon={FileText} accent="text-accent-amber" delta={3.7} deltaLabel="this week" />
        <MetricCard label="Cloudflare Workers" value={27} icon={Cloud} accent="text-accent-blue" />
        <MetricCard label="n8n Workflows"     value={39} unit="(1 verified)" icon={Workflow} accent="text-accent-rose" delta={-1} deltaLabel="degraded" />
        <MetricCard label="Local Ollama Models" value={13} unit="(+6 cloud)" icon={Sparkles} accent="text-accent-emerald" />
      </div>

      {/* Two-col: Services snapshot + activity */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 glass p-6 ring-glass">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Services Map</div>
              <h2 className="text-xl font-semibold mt-1">Live infrastructure</h2>
            </div>
            <Link to="/services" className="text-xs text-accent-blue hover:underline">View all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SERVICES.slice(0, 12).map((s, i) => (
              <motion.a
                key={s.id}
                href={s.url.startsWith("http") ? s.url : undefined}
                target={s.url.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03, duration: 0.4 }}
                className="group flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-white/[0.10] transition cursor-pointer"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  s.status === "healthy" ? "bg-accent-emerald/10 text-accent-emerald" :
                  s.status === "degraded" ? "bg-accent-amber/10 text-accent-amber" :
                  s.status === "broken" ? "bg-accent-rose/10 text-accent-rose" :
                  "bg-slate-500/10 text-slate-400"
                }`}>
                  <s.icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.name}</div>
                  <div className="text-[10px] font-mono text-slate-500 truncate">{s.platform}</div>
                </div>
                <span className={`block w-1.5 h-1.5 rounded-full ${
                  s.status === "healthy" ? "bg-accent-emerald" :
                  s.status === "degraded" ? "bg-accent-amber" :
                  s.status === "broken" ? "bg-accent-rose" : "bg-slate-500"
                }`} />
              </motion.a>
            ))}
          </div>
        </div>

        <div className="glass p-6 ring-glass">
          <div className="eyebrow">Recent Activity</div>
          <h2 className="text-xl font-semibold mt-1 mb-4">Live feed</h2>
          <div className="space-y-3">
            {recent.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex items-start gap-3 group"
              >
                <div className={`mt-1 w-2 h-2 rounded-full ${
                  r.kind === "deploy" ? "bg-accent-blue" :
                  r.kind === "ssh"    ? "bg-accent-cyan" :
                  r.kind === "auth"   ? "bg-accent-violet" :
                  r.kind === "ml"     ? "bg-accent-fuchsia" :
                  "bg-accent-emerald"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-slate-300 leading-snug">{r.text}</div>
                  <div className="eyebrow mt-1">{r.at}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
